import { INotificationMessage } from "@fl/shared/src/types/messages";
import { UsuarioAttributes } from "@fl/shared/src/types/models/suporte_fl/usuarios";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import noprofile from "../../../assets/no-profile.png";
import SelectAvatarModal from "../../../components/modals/selectAvatar";
import { useAuth } from "../../../context/auth";
import { usuarioSchema } from "../../../schemas/users";
import ApiUsersPrivate from "../../../services/suporte/auth/users";
import StorageItems from "../../../storage";
import DefaultBasePage from "../../default/base";

export type UsuarioFormInputs = Omit<
  UsuarioAttributes,
  | "id_usuario"
  | "createdAt"
  | "updatedAt"
  | "id_grupo_permissao"
  | "ativo"
  | "codigo"
  | "expiracao"
  | "banido"
  | "master"
> & {
  confirmarSenha: string;
};

const ConfigProfilePrivatePage = () => {
  const { authToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState<string | ArrayBuffer | null>(null);
  const [profileImage, setProfileImage] = useState<any | null>(null);

  useEffect(() => {
    setValue("nome", authToken!.nome);
    setValue("email", authToken!.email);
    const avatar = new StorageItems().get("avatar");
    if (avatar) setProfileImage(avatar);
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<UsuarioFormInputs>({
    resolver: yupResolver(usuarioSchema),
  });

  const onSubmit: SubmitHandler<UsuarioFormInputs> = async (data) => {
    try {
      setLoading(true);
      const result = await new ApiUsersPrivate({ data }).update(
        authToken && authToken.id_usuario ? authToken.id_usuario.toString() : ""
      );
      if (result.message) setMessages(result.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    reset();
  };

  const onResultCrop = async (data: any) => {
    try {
      setLoading(true);

      if (data) {
        setProfileImage(data);
        const result = await new ApiUsersPrivate({
          id_usuario: authToken?.id_usuario,
        }).uploadAvatar(data);

        if (result.success) {
          new StorageItems().set("avatar", data);
        }

        if (result.message && result.message.length > 0)
          setMessages(result.message);
      }
    } finally {
      setLoading(false);
      setShowCropModal(false);
    }
  };

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target) {
          setCropImage(e.target.result);
          setShowCropModal(true);
        }
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  return (
    <DefaultBasePage
      title={authToken?.nome}
      loading={loading}
      message={messages}
    >
      <div className="text-justify">
        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Row className="mt-2">
            <Col lg={2}>
              <div className="d-flex justify-content-center">
                <div className="">
                  <img
                    className="profile-image"
                    src={profileImage ? profileImage : noprofile}
                    width={100}
                    height={100}
                  />
                  <div
                    className="text-center hover-bold pointer"
                    onClick={() => {
                      document.getElementById("selectImage")?.click();
                    }}
                  >
                    Alterar avatar
                  </div>
                  <input
                    id="selectImage"
                    hidden
                    type="file"
                    multiple={false}
                    accept="image/png, image/jpeg"
                    onChange={onSelectFile}
                  />
                </div>
              </div>
            </Col>
            <Col className="mt-1">
              <Form.Group controlId="formNome">
                <Form.Label>Nome Completo:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o nome"
                  {...register("nome")}
                  isInvalid={!!errors.nome}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.nome?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col className="mt-1">
              <Form.Group controlId="formEmail">
                <Form.Label>E-mail:</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Digite o email"
                  {...register("email")}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col className="mt-1">
              <Form.Group controlId="formSenha">
                <Form.Label>Nova Senha:</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Digite a senha"
                  {...register("senha")}
                  isInvalid={!!errors.senha}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.senha?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col className="mt-1">
              <Form.Group controlId="formConfirmarSenha">
                <Form.Label>Confirmar Nova Senha:</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirme a senha"
                  {...register("confirmarSenha")}
                  isInvalid={!!errors.confirmarSenha}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.confirmarSenha?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-between">
            <Button
              variant="outline-danger"
              type="submit"
              className="mt-4"
              onClick={handleReset}
            >
              Limpar Dados
            </Button>
            <Button variant="outline-success" type="submit" className="mt-4">
              Atualizar Usuário
            </Button>
          </div>
        </Form>
      </div>

      <SelectAvatarModal
        showModal={showCropModal}
        image={cropImage}
        onResultCrop={onResultCrop}
      />
    </DefaultBasePage>
  );
};

export default ConfigProfilePrivatePage;
